import { For, Show, createMemo, createSignal } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { INBOX_SLUG, composeTags, projectDisplay } from "../lib/tags";
import { localToday } from "../lib/today";
import { createTicket, tickets } from "../store/tickets";
import {
  quickAddOpen,
  setQuickAddOpen,
  setSelectedId,
} from "../store/ui";
import styles from "./QuickAddModal.module.css";

// Human-created tickets are always stamped actor:human (AGENT.md §7). The
// project is whatever the operator types/picks; composeTags strips whitespace
// and prefixes proj: to satisfy the LIKE-based backend matching.
const ACTOR_HUMAN = "human";

export function QuickAddModal() {
  const [title, setTitle] = createSignal("");
  const [project, setProject] = createSignal("");
  const [error, setError] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  const navigate = useNavigate();

  // Projects are derived from existing tickets' proj: tags — same logic as the
  // Sidebar. There is no project entity table. Inbox is excluded: you reach it
  // by leaving the field empty, never by picking it, and surfacing it would let
  // a user type "未分類" and spawn a real second project distinct from inbox.
  const projects = createMemo(() => {
    const set = new Set<string>();
    for (const t of tickets()) {
      if (t.project && t.project !== INBOX_SLUG) set.add(t.project);
    }
    return [...set].sort();
  });

  const suggestions = createMemo(() => {
    const q = project().trim().toLowerCase();
    if (!q) return projects();
    return projects().filter((p) => p.toLowerCase().includes(q));
  });

  // Show "+ create" when the typed value matches no existing project. On the
  // very first ticket (no projects yet) this is the only affordance shown.
  const showCreate = createMemo(() => {
    const q = project().trim();
    if (!q) return false;
    return !projects().some((p) => p.toLowerCase() === q.toLowerCase());
  });

  // Project is optional now — empty falls back to inbox (未分類). Only the
  // title gates submission.
  const canSubmit = () => title().trim() !== "" && !submitting();

  const reset = () => {
    setTitle("");
    setProject("");
    setError("");
    setSubmitting(false);
  };

  const close = () => {
    setQuickAddOpen(false);
    reset();
  };

  const submit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    setError("");
    try {
      const created = await createTicket({
        title: title().trim(),
        status: "todo",
        priority: 1,
        end_date: localToday(),
        tags: composeTags({
          project: project().trim() || INBOX_SLUG,
          actor: ACTOR_HUMAN,
          labels: [],
        }),
      });
      // Navigate to the ticket's project view so it's immediately visible in
      // the list even after the SidePanel is closed. created.project is always
      // set (empty input fell back to inbox), so the slug is never null here.
      navigate(`/project/${created.project ?? INBOX_SLUG}`);
      // Open the SidePanel so the user can fill in body/dates/priority.
      setSelectedId(created.id);
      close();
    } catch {
      // Keep the modal open and the typed input intact so nothing is lost;
      // createTicket is non-optimistic, so a failed POST means no row exists.
      setSubmitting(false);
      setError("建立失敗,請重試(可能是 token 失效)");
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.isComposing) return; // IME candidate selection — don't intercept
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Show when={quickAddOpen()}>
      <div class={styles.overlay} onClick={close}>
        <div
          class={styles.panel}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={onKeyDown}
        >
          <input
            class={styles.titleInput}
            type="text"
            placeholder="工單標題…"
            value={title()}
            onInput={(e) => setTitle(e.currentTarget.value)}
            ref={(el) => setTimeout(() => el?.focus(), 0)}
          />

          <div class={styles.projectField}>
            <input
              class={styles.projectInput}
              type="text"
              placeholder="專案(留空 = 未分類)…"
              value={project()}
              onInput={(e) => setProject(e.currentTarget.value)}
            />
            <Show when={suggestions().length > 0 || showCreate()}>
              <div class={styles.suggestions}>
                <For each={suggestions()}>
                  {(p) => (
                    <button
                      type="button"
                      class={styles.suggestion}
                      onClick={() => setProject(p)}
                    >
                      <span
                        class={styles.dot}
                        style={{ background: "var(--c-accent)" }}
                      />
                      {projectDisplay(p)}
                    </button>
                  )}
                </For>
                <Show when={showCreate()}>
                  <div class={styles.createHint}>
                    ↵ 建立新專案「{project().trim()}」
                  </div>
                </Show>
              </div>
            </Show>
          </div>

          <Show when={error()}>
            <div class={styles.error}>{error()}</div>
          </Show>

          <div class={styles.footer}>
            <button
              type="button"
              class={styles.submit}
              disabled={!canSubmit()}
              onClick={submit}
            >
              建立工單
            </button>
            <span class={styles.hint}>
              <kbd class={styles.kbd}>↵</kbd> 建立
              <kbd class={styles.kbd}>esc</kbd> 關閉
            </span>
          </div>
        </div>
      </div>
    </Show>
  );
}
